import type { MessageKey } from '@dijitalapp/i18n';
import type {
  AssistantAnswerDto,
  AssistantDto,
  AssistantSettings,
  AssistantSourceDto,
  AssistantSourceKind,
  AssistantUnansweredDto,
} from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { NavLink } from 'react-router';
import { api, ApiError, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';
import { PageHeader } from './SectionNav';

type Tab = 'sources' | 'test' | 'settings' | 'unanswered';
const TABS: Tab[] = ['sources', 'test', 'settings', 'unanswered'];

/**
 * `/spaces/:id/assistant`: the organisation's DijitalApp Assistant. Knowledge sources, a test chat that
 * runs the same pipeline visitors will use, settings, and the questions it could not answer.
 */
export function AssistantManager({ spaceId, spaceName }: { spaceId: string; spaceName: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [data, setData] = useState<AssistantDto | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('sources');

  useEffect(() => {
    let alive = true;
    void api<AssistantDto>('GET', `/spaces/${spaceId}/assistant`).then(
      (d) => alive && setData(d),
      (err: unknown) => alive && setDenied(err instanceof ApiError && err.status === 403 ? t('assistant.notAllowed') : t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [spaceId, t]);

  const configured = data ? data.sources.length > 0 || data.settings.enabled : false;
  return (
    <div className="content-inner">
      <PageHeader
        title={t('assistant.title')}
        description={spaceName}
        actions={
          <NavLink className="btn btn-sm" to={`/spaces/${spaceId}`}>
            {t('assistant.backToSpace')}
          </NavLink>
        }
      />
      {denied && <p className="alert alert-error">{denied}</p>}
      {data && (
        <>
          <ProviderNotice providers={data.providers} />
          <div className="assistant-stats">
            <Stat label={t('assistant.stat.status')} value={data.settings.enabled ? t('assistant.on') : t('assistant.off')} tone={data.settings.enabled ? 'ok' : 'muted'} />
            <Stat label={t('assistant.stat.sources')} value={String(data.sources.length)} />
            <Stat label={t('assistant.stat.questions')} value={String(data.stats.questions)} />
            <Stat label={t('assistant.stat.unanswered')} value={String(data.stats.unanswered)} tone={data.stats.unanswered ? 'warn' : 'muted'} />
          </div>
          <div className="segmented assistant-tabs" role="tablist" aria-label={t('assistant.title')}>
            {TABS.map((x) => (
              <button key={x} type="button" role="tab" aria-selected={tab === x} onClick={() => setTab(x)}>
                {t(`assistant.tab.${x}` as MessageKey)}
              </button>
            ))}
          </div>
          {!configured && tab === 'sources' && <p className="hint">{t('assistant.gettingStarted')}</p>}
          {tab === 'sources' && <SourcesTab spaceId={spaceId} data={data} onChange={setData} />}
          {tab === 'test' && <TestTab spaceId={spaceId} data={data} />}
          {tab === 'settings' && <SettingsTab spaceId={spaceId} data={data} onChange={setData} />}
          {tab === 'unanswered' && <UnansweredTab spaceId={spaceId} />}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'ok' | 'warn' | 'muted' }) {
  return (
    <div className={`stat stat-${tone}`}>
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
    </div>
  );
}

function ProviderNotice({ providers }: { providers: AssistantDto['providers'] }) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (providers.ai && providers.embeddings) return null;
  return (
    <div className="alert alert-info assistant-notice" role="status">
      <strong>{t('assistant.setupTitle')}</strong>
      <ul>
        {!providers.ai && <li>{t('assistant.noAi')}</li>}
        {!providers.embeddings && <li>{t('assistant.noEmbeddings')}</li>}
      </ul>
    </div>
  );
}

function useRun() {
  const { i18n } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(i18n.t(errorMessageKey(err)));
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function SourcesTab({ spaceId, data, onChange }: { spaceId: string; data: AssistantDto; onChange: (d: AssistantDto) => void }) {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const { busy, error, run } = useRun();
  const [kind, setKind] = useState<AssistantSourceKind>('text');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [faq, setFaq] = useState([{ q: '', a: '' }]);
  const needsSettings = !data.settings.enabled && data.sources.length === 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body =
      kind === 'text' ? { kind, title, content } : kind === 'faq' ? { kind, title, items: faq.filter((f) => f.q.trim() && f.a.trim()) } : { kind, url, title };
    void run(async () => {
      const d = await api<AssistantDto>('POST', `/spaces/${spaceId}/assistant/sources`, body);
      onChange(d);
      setTitle('');
      setContent('');
      setUrl('');
      setFaq([{ q: '', a: '' }]);
    });
  };
  const act = (s: AssistantSourceDto, what: 'reindex' | 'delete') =>
    void run(async () =>
      onChange(
        what === 'delete'
          ? await api<AssistantDto>('DELETE', `/spaces/${spaceId}/assistant/sources/${s.id}`)
          : await api<AssistantDto>('POST', `/spaces/${spaceId}/assistant/sources/${s.id}/reindex`),
      ),
    );

  return (
    <div className="assistant-grid">
      <form className="card stack" onSubmit={submit}>
        <h2 className="card-title">{t('assistant.addSource')}</h2>
        {needsSettings && <p className="hint">{t('assistant.saveSettingsFirst')}</p>}
        <div className="segmented" role="radiogroup" aria-label={t('assistant.sourceKind')}>
          {(['text', 'faq', 'url'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}>
              {t(`assistant.kind.${k}` as MessageKey)}
            </button>
          ))}
        </div>
        <p className="hint">{t(`assistant.kindHint.${kind}` as MessageKey)}</p>
        {kind === 'url' ? (
          <div className="field">
            <label htmlFor="src-url">{t('assistant.url')}</label>
            <input id="src-url" className="input" type="url" required placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
        ) : (
          <div className="field">
            <label htmlFor="src-title">{t('assistant.sourceTitle')}</label>
            <input id="src-title" className="input" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
        )}
        {kind === 'text' && (
          <div className="field">
            <label htmlFor="src-content">{t('assistant.content')}</label>
            <textarea id="src-content" className="input" rows={10} required maxLength={200_000} value={content} onChange={(e) => setContent(e.target.value)} />
          </div>
        )}
        {kind === 'faq' && (
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            {faq.map((f, i) => (
              <div key={i} className="faq-pair card-inset">
                <input className="input" aria-label={t('assistant.question')} placeholder={t('assistant.question')} value={f.q} onChange={(e) => setFaq(faq.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))} />
                <textarea className="input" rows={2} aria-label={t('assistant.answer')} placeholder={t('assistant.answer')} value={f.a} onChange={(e) => setFaq(faq.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} />
                {faq.length > 1 && (
                  <button type="button" className="btn btn-sm" onClick={() => setFaq(faq.filter((_, j) => j !== i))}>
                    {t('assistant.removePair')}
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-sm" onClick={() => setFaq([...faq, { q: '', a: '' }])}>
              + {t('assistant.addPair')}
            </button>
          </div>
        )}
        {error && <p className="alert alert-error">{error}</p>}
        <div className="row">
          <span className="spacer" />
          <button className="btn btn-primary" disabled={busy}>
            {busy ? t('assistant.indexing') : t('assistant.add')}
          </button>
        </div>
      </form>

      <section className="card stack" aria-labelledby="src-list">
        <h2 id="src-list" className="card-title">
          {t('assistant.sources', { count: data.sources.length })}
        </h2>
        {data.sources.length === 0 && <p className="muted">{t('assistant.noSources')}</p>}
        <ul className="plain-list source-list">
          {data.sources.map((s) => (
            <li key={s.id} className="source-row">
              <span className="source-kind" aria-hidden>
                <Icon name={s.kind === 'url' ? 'discover' : s.kind === 'faq' ? 'info' : 'file'} size={18} />
              </span>
              <div className="source-main">
                <strong>{s.title}</strong>
                <span className="muted">
                  {t(`assistant.kind.${s.kind}` as MessageKey)} · {t('assistant.chunks', { count: s.chunks })}
                  {data.providers.embeddings ? ` · ${t('assistant.embedded', { count: s.embedded })}` : ''} · {formatDate(s.updatedAt, { dateStyle: 'medium' })}
                </span>
                {s.error && <span className="source-error">{s.error}</span>}
              </div>
              <span className={`badge ${s.status === 'ready' ? 'badge-success' : s.status === 'failed' ? 'badge-danger' : ''}`}>{t(`assistant.status.${s.status}` as MessageKey)}</span>
              <div className="source-actions">
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act(s, 'reindex')}>
                  {t('assistant.reindex')}
                </button>
                <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => act(s, 'delete')}>
                  {t('assistant.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

interface Turn {
  question: string;
  answer: AssistantAnswerDto | null;
}

function TestTab({ spaceId, data }: { spaceId: string; data: AssistantDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { busy, error, run } = useRun();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [question, setQuestion] = useState('');

  const ask = (e: FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setQuestion('');
    setTurns((x) => [...x, { question: q, answer: null }]);
    void run(async () => {
      const a = await api<AssistantAnswerDto>('POST', `/spaces/${spaceId}/assistant/test`, { question: q, conversationId });
      setConversationId(a.conversationId);
      setTurns((x) => x.map((tn, i) => (i === x.length - 1 ? { ...tn, answer: a } : tn)));
    });
  };

  return (
    <section className="card stack assistant-test">
      <div className="row">
        <h2 className="card-title">{t('assistant.testTitle', { name: data.settings.name })}</h2>
        <span className="spacer" />
        <button type="button" className="btn btn-sm" disabled={!turns.length} onClick={() => (setTurns([]), setConversationId(undefined))}>
          {t('assistant.newChat')}
        </button>
      </div>
      <p className="hint">{t('assistant.testHint')}</p>
      <div className="test-thread" aria-live="polite">
        {data.settings.welcome && <p className="msg-a">{data.settings.welcome}</p>}
        {turns.map((tn, i) => (
          <div key={i} className="test-turn">
            <p className="msg-q">{tn.question}</p>
            {!tn.answer ? (
              <p className="msg-a muted">{t('assistant.thinking')}</p>
            ) : (
              <div className={`msg-a msg-${tn.answer.status}`}>
                {tn.answer.status === 'ai_unavailable' ? <p className="muted">{t('assistant.retrievalOnly')}</p> : <p>{tn.answer.text}</p>}
                {tn.answer.status !== 'answered' && tn.answer.status !== 'ai_unavailable' && <span className="badge">{t('assistant.handedOver')}</span>}
                {tn.answer.citations.length > 0 && (
                  <ul className="citations">
                    {tn.answer.citations.map((c) => (
                      <li key={`${c.n}-${c.sourceId}`}>
                        <details>
                          <summary>
                            [{c.n}] {c.title}
                          </summary>
                          <p>{c.excerpt}</p>
                        </details>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <p className="alert alert-error">{error}</p>}
      <form className="row test-composer" onSubmit={ask}>
        <input className="input" aria-label={t('assistant.askLabel')} placeholder={t('assistant.askLabel')} maxLength={1000} value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button className="btn btn-primary" disabled={busy || !question.trim()}>
          {t('assistant.send')}
        </button>
      </form>
    </section>
  );
}

function SettingsTab({ spaceId, data, onChange }: { spaceId: string; data: AssistantDto; onChange: (d: AssistantDto) => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { busy, error, run } = useRun();
  const [s, setS] = useState<AssistantSettings>(data.settings);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof AssistantSettings>(k: K, v: AssistantSettings[K]) => {
    setSaved(false);
    setS((x) => ({ ...x, [k]: v }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      onChange(await api<AssistantDto>('PUT', `/spaces/${spaceId}/assistant`, s));
      setSaved(true);
    });
  };
  const text = (k: 'name' | 'welcome' | 'handoffMessage' | 'blockedTopics', max: number) => (
    <div className="field">
      <label htmlFor={`as-${k}`}>{t(`assistant.field.${k}` as MessageKey)}</label>
      <input id={`as-${k}`} className="input" maxLength={max} required={k === 'name'} value={s[k]} onChange={(e) => set(k, e.target.value)} />
      <span className="hint">{t(`assistant.fieldHint.${k}` as MessageKey)}</span>
    </div>
  );
  return (
    <form className="assistant-grid" onSubmit={submit}>
      <section className="card stack">
        <h2 className="card-title">{t('assistant.basics')}</h2>
        <label className="check">
          <input type="checkbox" checked={s.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          <span>{t('assistant.field.enabled')}</span>
        </label>
        {text('name', 60)}
        <div className="field">
          <label htmlFor="as-tone">{t('assistant.field.tone')}</label>
          <select id="as-tone" className="input" value={s.tone} onChange={(e) => set('tone', e.target.value as AssistantSettings['tone'])}>
            {(['friendly', 'formal', 'concise'] as const).map((x) => (
              <option key={x} value={x}>
                {t(`assistant.tone.${x}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        {text('welcome', 300)}
        {text('handoffMessage', 300)}
      </section>
      <section className="card stack">
        <h2 className="card-title">{t('assistant.behaviour')}</h2>
        {text('blockedTopics', 1000)}
        <div className="field">
          <label htmlFor="as-instructions">{t('assistant.field.instructions')}</label>
          <textarea id="as-instructions" className="input" rows={8} maxLength={2000} value={s.instructions} onChange={(e) => set('instructions', e.target.value)} />
          <span className="hint">{t('assistant.fieldHint.instructions')}</span>
        </div>
        {error && <p className="alert alert-error">{error}</p>}
        {saved && <p className="alert alert-info">{t('assistant.saved')}</p>}
        <div className="row">
          <span className="spacer" />
          <button className="btn btn-primary" disabled={busy}>
            {t('assistant.save')}
          </button>
        </div>
      </section>
    </form>
  );
}

function UnansweredTab({ spaceId }: { spaceId: string }) {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const [rows, setRows] = useState<AssistantUnansweredDto[] | null>(null);
  useEffect(() => {
    void api<{ questions: AssistantUnansweredDto[] }>('GET', `/spaces/${spaceId}/assistant/unanswered`).then((r) => setRows(r.questions));
  }, [spaceId]);
  return (
    <section className="card stack">
      <h2 className="card-title">{t('assistant.tab.unanswered')}</h2>
      <p className="hint">{t('assistant.unansweredHint')}</p>
      {rows === null && <p className="muted">{t('app.loading')}</p>}
      {rows?.length === 0 && <p className="muted">{t('assistant.noUnanswered')}</p>}
      <ul className="plain-list">
        {rows?.map((r) => (
          <li key={r.question} className="toggle-row">
            <span>{r.question}</span>
            <span className="spacer" />
            <span className="muted">
              {t('assistant.times', { count: r.count })} · {formatDate(r.askedAt, { dateStyle: 'medium' })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
